import { del, get, patch, post, put, type SessionUser } from './client'
import type { LogoOrganisation } from './org'
import type { ApparenceBrute } from './apparence'
import type { ReglagesApparence } from '@/apparence/reglages'

/**
 * Périmètre PLATEFORME : l'annuaire des organisations.
 *
 * Module SÉPARÉ de `org.ts`, et pas une fonction de plus dedans : ces appels
 * traversent les tenants et n'existent que pour l'organisation de gestion. Les
 * mêler aux appels ordinaires ferait perdre cette distinction au premier coup
 * d'œil — côté serveur, la séparation est la même (`core/superuser/`).
 */

export interface OrganisationPlateforme {
  id: string
  name: string
  slug: string
  isHyperadmin: boolean
  createdAt: string
  /** Version du logo (ISO), `null` sans logo. Les octets se demandent à part. */
  logoUpdatedAt: string | null
  memberCount: number
  /**
   * Compteurs MÉTIER enregistrés par les domaines côté serveur
   * (`registerOrgCounter`), indexés par leur clé — aujourd'hui `items`. Une
   * clé absente signifie que le domaine correspondant n'existe pas dans ce
   * déploiement : d'où le `Record` et le `?? 0` à l'affichage, jamais un
   * accès direct qui lèverait `undefined` en `NaN`.
   */
  counters: Record<string, number>
}

export interface PageOrganisations {
  organisations: OrganisationPlateforme[]
  total: number
}

export function listOrganisations(params: {
  limit?: number
  offset?: number
  search?: string
} = {}): Promise<PageOrganisations> {
  const requete = new URLSearchParams()
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Une recherche vide ne doit PAS partir : le serveur la refuserait (min 1
  // caractère après trim), et l'utilisateur verrait une erreur de validation
  // pour avoir simplement effacé son champ.
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)

  const suffixe = requete.toString()
  return get<PageOrganisations>(`/superuser/organisations${suffixe === '' ? '' : `?${suffixe}`}`)
}

/**
 * Renomme une organisation depuis le périmètre plateforme.
 *
 * Retourne l'organisation sans ses compteurs : le serveur ne les recalcule pas
 * sur une mise à jour, et les inventer ici (zéro, ou l'ancienne valeur) ferait
 * afficher un chiffre faux comme s'il venait de la base.
 */
export function updateOrganisation(
  id: string,
  corps: { name: string },
): Promise<Omit<OrganisationPlateforme, 'memberCount' | 'counters'>> {
  return patch(`/superuser/organisations/${id}`, corps)
}

/**
 * « Jump » backoffice : entrer dans l'organisation d'un client depuis
 * l'annuaire, sans y avoir de compte. Le corps est VIDE : la cible est dans le
 * chemin, et l'origine est celle de la session côté serveur — jamais envoyée.
 *
 * Le droit est vérifié SERVEUR (organisation de gestion + rang owner/admin),
 * puis revalidé à chaque requête suivante. Le bouton ne fait que refléter cette
 * garde. La réponse est le profil de session EFFECTIF — celui de `/auth/me` —
 * à appliquer tel quel : organisation du client, `jump` renseigné.
 *
 * Retour : `quitterJump()` dans `auth.ts`, hors du périmètre plateforme.
 */
export function jumpOrganisation(id: string): Promise<SessionUser> {
  return post<SessionUser>(`/superuser/organisations/${id}/jump`)
}

/** Logo d'une organisation, pour l'aperçu du tiroir (404 s'il n'y en a pas). */
export function getLogoOrganisationPlateforme(id: string): Promise<LogoOrganisation> {
  return get<LogoOrganisation>(`/superuser/organisations/${id}/logo`)
}

/**
 * Dépose ou remplace le logo. `data` est le base64 NU du fichier : le serveur
 * en déduit le format, et refuse en 422 tout ce qui n'est ni PNG, ni JPEG, ni
 * WebP — un SVG renommé compris.
 */
export function deposerLogoOrganisation(
  id: string,
  data: string,
): Promise<{ logoUpdatedAt: string; avertissements: string[] }> {
  return put(`/superuser/organisations/${id}/logo`, { data })
}

/** Retire le logo. Idempotent côté serveur : 204 même sans logo. */
export function retirerLogoOrganisation(id: string): Promise<void> {
  return del(`/superuser/organisations/${id}/logo`)
}

// ---------------------------------------------------------------------------
// Catalogue des offres.
// ---------------------------------------------------------------------------

/** Les deux natures d'offre — miroir de `NATURES_PLAN` côté serveur. */
export type NaturePlan = 'subscription' | 'one_time'
/** Fréquences d'un abonnement ; `null` pour un ponctuel. */
export type IntervallePlan = 'month' | 'year'

export interface PlanPlateforme {
  id: string
  code: string
  kind: NaturePlan
  name: string
  description: string
  /** Centimes — jamais de flottant pour un prix (voir migration 0012). */
  amountCents: number
  currency: string
  billingInterval: IntervallePlan | null
  features: string[]
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface PagePlans {
  plans: PlanPlateforme[]
  total: number
}

export interface NouveauPlan {
  code: string
  kind: NaturePlan
  name: string
  description?: string
  amountCents: number
  currency?: string
  billingInterval?: IntervallePlan | null
  features?: string[]
  sortOrder?: number
}

export function listPlans(
  params: { limit?: number; offset?: number; search?: string } = {},
): Promise<PagePlans> {
  const requete = new URLSearchParams()
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Même règle que l'annuaire : une recherche blanche ne part pas (le serveur
  // la refuserait en validation).
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)

  const suffixe = requete.toString()
  return get<PagePlans>(`/superuser/plans${suffixe === '' ? '' : `?${suffixe}`}`)
}

export function createPlan(corps: NouveauPlan): Promise<PlanPlateforme> {
  return post('/superuser/plans', corps)
}

/**
 * Modification partielle. `code` n'est PAS dans le type : l'identifiant d'un
 * plan est immuable (référé par les intégrations) — le serveur refuse la clé.
 */
export function updatePlan(
  id: string,
  corps: Partial<Omit<NouveauPlan, 'code'>> & { isActive?: boolean },
): Promise<PlanPlateforme> {
  return patch(`/superuser/plans/${id}`, corps)
}

// ---------------------------------------------------------------------------
// Connexions aux services externes (onglets de l'écran Services).
// ---------------------------------------------------------------------------

export interface HoteMail {
  host: string
  port: number
  secure: boolean
  /** Identifiant de compte — NON secret : seul le mot de passe est chiffré. */
  user: string
}

export interface ConnexionService {
  id: string
  code: string
  kind: string
  label: string
  /** Réglages non secrets (hôtes, ports, utilisateurs). */
  settings: { smtp?: HoteMail; imap?: HoteMail }
  /**
   * Drapeaux « un tel secret est enregistré » — clés de paquet → `true`.
   * La VALEUR ne quitte jamais le serveur : le mot de passe d'un relais SMTP
   * n'a rien à faire dans les outils de développement d'un navigateur.
   */
  secrets: Record<string, boolean>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PageConnections {
  connections: ConnexionService[]
  total: number
}

export function listConnections(
  kind: string,
  params: { limit?: number; offset?: number; search?: string } = {},
): Promise<PageConnections> {
  const requete = new URLSearchParams({ kind })
  if (params.limit !== undefined) requete.set('limit', String(params.limit))
  if (params.offset !== undefined) requete.set('offset', String(params.offset))
  // Même règle que partout : une recherche blanche ne part pas.
  const recherche = params.search?.trim() ?? ''
  if (recherche !== '') requete.set('search', recherche)
  return get<PageConnections>(`/superuser/services/connections?${requete.toString()}`)
}

export interface NouvelleBoiteMail {
  code: string
  label: string
  smtp: HoteMail
  imap?: HoteMail
  smtpPassword: string
  imapPassword?: string
}

export function createMailBox(corps: NouvelleBoiteMail): Promise<ConnexionService> {
  return post('/superuser/services/connections', { kind: 'mail', ...corps })
}

/**
 * Modification partielle d'une boîte mail. Triplet par champ : ABSENT du corps
 * = inchangé ; `null` = effacé (config IMAP, mot de passe) ; valeur = remplacé.
 */
export interface ModificationBoiteMail {
  label?: string
  smtp?: HoteMail
  imap?: HoteMail | null
  smtpPassword?: string | null
  imapPassword?: string | null
  isActive?: boolean
}

export function updateMailBox(
  id: string,
  corps: ModificationBoiteMail,
): Promise<ConnexionService> {
  return patch(`/superuser/services/connections/${id}`, corps)
}

/**
 * Apparence de la plateforme : REMPLACE toutes les surcharges (couleurs,
 * arrondis, polices, boutons, densité, ombres, barre du haut, en-têtes de section) — ce qui est absent du corps revient à sa
 * valeur par défaut. La relecture, publique, est `getApparence` (`api/apparence.ts`).
 *
 * Le corps est typé `ReglagesApparence` et non laissé implicite : construit
 * champ par champ, il a perdu `fonts` en silence, et le serveur, qui l'exige,
 * refusait alors tout enregistrement. Typé, un réglage oublié ne compile plus.
 */
export function updateApparence(reglages: ReglagesApparence): Promise<ApparenceBrute> {
  const corps: ReglagesApparence = {
    colors: reglages.colors,
    radii: reglages.radii,
    fonts: reglages.fonts,
    buttons: reglages.buttons,
    density: reglages.density,
    shadows: reglages.shadows,
    header: reglages.header,
    sections: reglages.sections,
  }
  return put('/superuser/apparence', corps)
}
